import { ButtonHTMLAttributes, forwardRef, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconChevronDownSmall } from '@components/icons/chevron-down-small';
import { IconHired } from '@components/icons/hired';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconReject } from '@components/icons/reject';
import { IconStages } from '@components/icons/stages';
import { Menu } from '@components/menu';
import { Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

import { JobTrigger, TriggerType } from './api';
import { useDeleteJobTrigger, useJobTriggers, useUpdateJobTrigger } from './hooks';
import { useJourneySelectorModal } from './journey-selector-modal';
import {
  TRIGGER_MENU_WIDTH,
  TriggerActionMenuItems,
  TriggerUnavailableBadge,
  useRemoveTriggerConfirmation,
} from './trigger-action-menu-items';
import { useTriggerConfigSidebar } from './trigger-config-sidebar';
import { useAddStageTrigger } from './use-add-stage-trigger';
import { JourneyOutcome } from './use-stage-entry-journey';

import { JourneyListError, JourneyListLoading } from '../components/list-states';
import { RawButton } from '../components/raw-button';

export type JobStageInfo = {
  id: number;
  name: string;
  /** Non-empty when moving into this stage auto-opens a modal of its own
   * (e.g. HIRE_APPLICANT) — the journey prompt chains onto that modal's
   * close instead of being blocked, see use-stage-entry-journey.ts. */
  dynamicStage: unknown[];
};

export type TriggersSidebarProps = {
  jobId: number;
  jobStages: JobStageInfo[];
  isAdmin: boolean;
};

export const TriggersSidebar = ({ jobId, jobStages, isAdmin }: TriggersSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const triggersQuery = useJobTriggers(jobId);
  const deleteTrigger = useDeleteJobTrigger(jobId);
  const updateTrigger = useUpdateJobTrigger(jobId);
  const showSelectorModal = useJourneySelectorModal();
  const confirmRemoveTrigger = useRemoveTriggerConfirmation();
  const { addStageTrigger } = useAddStageTrigger(jobId);
  const openConfigSidebar = useTriggerConfigSidebar();
  const showTriggersSidebar = useSidebar(TriggersSidebar, {
    variant: 'REGULAR',
    hasBackdrop: true,
  });

  // Replace and remove act on one existing trigger, so only that row freezes
  // while its mutation is in flight rather than every row at once. Adding a
  // journey needs no slot here: it persists nothing until the config sidebar
  // is saved, and that sidebar has replaced this one by then.
  const [mutatingTriggerId, setMutatingTriggerId] = useState<number | null>(null);

  // Only one right-side panel exists at a time, so opening the config sidebar
  // replaces this one. onClose swaps the triggers panel back in after the
  // config panel is dismissed (Cancel or successful Save).
  const openConfigForJourney = (
    journeyId: string,
    journeyTitle: string,
    pendingTrigger?: { triggerType: TriggerType; jobStage: number | null },
  ) => {
    openConfigSidebar({
      jobId,
      journeyId,
      journeyTitle,
      pendingTrigger,
      onClose: () => showTriggersSidebar({ jobId, jobStages, isAdmin }),
    });
  };

  const triggers = triggersQuery.data ?? [];
  const stageTriggers = triggers.filter((trigger) => trigger.triggerType === 'stage');

  const openSelectorForStage = (stageId: number) => {
    addStageTrigger(stageId, {
      isAdmin,
      onConfigClose: () => showTriggersSidebar({ jobId, jobStages, isAdmin }),
    });
  };

  const openSelectorForOutcome = (outcomeType: JourneyOutcome) => {
    showSelectorModal({
      jobId,
      triggerType: outcomeType,
      jobStage: null,
      isAdmin,
      onSelect: (journey) => {
        openConfigForJourney(journey.journeyId, journey.title, {
          triggerType: outcomeType,
          jobStage: null,
        });
      },
    });
  };

  const openSelectorForReplace = (trigger: JobTrigger) => {
    showSelectorModal({
      jobId,
      triggerType: trigger.triggerType,
      jobStage: trigger.jobStage,
      isAdmin,
      onSelect: (journey) => {
        setMutatingTriggerId(trigger.id);
        updateTrigger.mutate({
          triggerId: trigger.id,
          journeyId: journey.journeyId,
          onSettled: () => setMutatingTriggerId(null),
        });
      },
    });
  };

  const handleDelete = (trigger: JobTrigger, rowLabel: string) => {
    confirmRemoveTrigger(trigger, rowLabel, () => {
      setMutatingTriggerId(trigger.id);
      return deleteTrigger.mutateAsync({
        triggerId: trigger.id,
        onSettled: () => setMutatingTriggerId(null),
      });
    });
  };

  const getStageName = (stageId: number | null) => {
    if (stageId === null) {
      return t('unknownStage');
    }
    return jobStages.find((stage) => stage.id === stageId)?.name ?? t('unknownStage');
  };

  const triggerByStageId = new Map(stageTriggers.map((trigger) => [trigger.jobStage, trigger]));
  // A stage trigger whose stage ID is not in the current jobStages list means
  // the stages were updated in another tab after the page loaded. These rows
  // remain visible so the trigger can still be replaced or removed.
  const orphanStageTriggers = stageTriggers.filter(
    (trigger) => !jobStages.some((stage) => stage.id === trigger.jobStage),
  );

  const hiredTrigger = triggers.find((trigger) => trigger.triggerType === 'hired');
  const rejectedTrigger = triggers.find((trigger) => trigger.triggerType === 'rejected');

  // Only a row that holds a trigger can be mid-mutation; an empty row's
  // "Add journey" starts no write of its own.
  const isRowMutating = (trigger: JobTrigger | undefined) =>
    trigger !== undefined && mutatingTriggerId === trigger.id;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('title')} icon={IconJourneyItem} iconColor="blue100" />
      <Sidebar.Content data-cy="journey-triggers-sidebar">
        {triggersQuery.isLoading ? (
          <JourneyListLoading />
        ) : triggersQuery.isError ? (
          <JourneyListError
            description={t('loadError')}
            retryLabel={t('retry')}
            onRetry={() => triggersQuery.refetch()}
          />
        ) : (
          <Stack space={6}>
            {/* Pipeline stage triggers group */}
            <Stack data-cy="stage-triggers-group" space={3}>
              <Stack space={1}>
                <Text font="bold16" color="textPrimary" text={t('stageTriggers')} />
                <Text font="regular14" color="textSecondary" text={t('stageTriggersDescription')} />
              </Stack>
              {jobStages.map((stage) => (
                <StageTriggerRow
                  key={stage.id}
                  stageName={stage.name}
                  trigger={triggerByStageId.get(stage.id)}
                  onAdd={() => openSelectorForStage(stage.id)}
                  onReplace={openSelectorForReplace}
                  onDelete={handleDelete}
                  onEditConfig={openConfigForJourney}
                  isMutating={isRowMutating(triggerByStageId.get(stage.id))}
                />
              ))}
              {orphanStageTriggers.map((trigger) => (
                <StageTriggerRow
                  key={trigger.id}
                  stageName={getStageName(trigger.jobStage)}
                  trigger={trigger}
                  onReplace={openSelectorForReplace}
                  onDelete={handleDelete}
                  onEditConfig={openConfigForJourney}
                  isMutating={mutatingTriggerId === trigger.id}
                />
              ))}
              {jobStages.length === 0 && stageTriggers.length === 0 && (
                <EmptyRow>
                  <Text font="regular14" color="textHelper" text={t('noStages')} />
                </EmptyRow>
              )}
            </Stack>

            {/* Final outcome triggers group */}
            <Stack data-cy="final-outcome-triggers-group" space={3}>
              <Stack space={1}>
                <Text font="bold16" color="textPrimary" text={t('outcomeTriggers')} />
                <Text
                  font="regular14"
                  color="textSecondary"
                  text={t('outcomeTriggersDescription')}
                />
              </Stack>
              <OutcomeRow
                data-cy="outcome-trigger-row-hired"
                trigger={hiredTrigger}
                label={t('hired')}
                subtitle={t('hiredSubtitle')}
                outcomeType="hired"
                onAdd={() => openSelectorForOutcome('hired')}
                onReplace={openSelectorForReplace}
                onDelete={handleDelete}
                onEditConfig={openConfigForJourney}
                isMutating={isRowMutating(hiredTrigger)}
              />
              <OutcomeRow
                data-cy="outcome-trigger-row-rejected"
                trigger={rejectedTrigger}
                label={t('rejected')}
                subtitle={t('rejectedSubtitle')}
                outcomeType="rejected"
                onAdd={() => openSelectorForOutcome('rejected')}
                onReplace={openSelectorForReplace}
                onDelete={handleDelete}
                onEditConfig={openConfigForJourney}
                isMutating={isRowMutating(rejectedTrigger)}
              />
            </Stack>
          </Stack>
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

// ---- Internal sub-components ----

type TriggerRowActionsProps = {
  trigger?: JobTrigger;
  onAdd?: () => void;
  onReplace: (trigger: JobTrigger) => void;
  onDelete: (trigger: JobTrigger, rowLabel: string) => void;
  onEditConfig: (journeyId: string, journeyTitle: string) => void;
  isMutating?: boolean;
};

// The rows own their label ("Stage 2", "Hired") and hand it to the actions so
// the remove confirmation can name the slot, not just the journey.
type TriggerRowActionsInnerProps = TriggerRowActionsProps & { rowLabel: string };

/**
 * Right side of every row: ghost "+ Add journey" when the slot is empty,
 * or the green journey chip (which doubles as the row-menu trigger) when
 * a trigger exists.
 */
const TriggerRowActions = ({
  trigger,
  rowLabel,
  onAdd,
  onReplace,
  onDelete,
  onEditConfig,
  isMutating,
}: TriggerRowActionsInnerProps) => {
  const { t } = useScopedTranslate(locale);

  if (!trigger) {
    return (
      <Button
        dataCy="add-journey-trigger-button"
        variant="ghost"
        size="sm"
        iconLeft={<IconAddPlus />}
        text={t('addJourney')}
        onClick={onAdd}
        disabled={isMutating}
      />
    );
  }

  return (
    <RowActions orientation="horizontal" align="center" space={2}>
      {!trigger.isAvailable && <TriggerUnavailableBadge dataCy="trigger-unavailable-badge" />}
      <Menu
        trigger={JourneyChipTrigger}
        width={TRIGGER_MENU_WIDTH}
        triggerProps={{ journeyTitle: trigger.journeyTitle, dataCy: 'trigger-row-menu-button' }}
      >
        <TriggerActionMenuItems
          trigger={trigger}
          onEditConfig={() => onEditConfig(trigger.journeyId, trigger.journeyTitle)}
          onReplace={() => onReplace(trigger)}
          onDelete={() => onDelete(trigger, rowLabel)}
          isMutating={Boolean(isMutating)}
          dataCyPrefix="trigger-row-menu"
        />
      </Menu>
    </RowActions>
  );
};

type StageTriggerRowProps = TriggerRowActionsProps & {
  stageName: string;
};

const StageTriggerRow = ({ stageName, trigger, ...actionProps }: StageTriggerRowProps) => (
  <Card
    spaceX={4}
    spaceY={3}
    data-cy={trigger ? 'stage-trigger-row' : undefined}
    data-stage={trigger ? trigger.jobStage : undefined}
  >
    <Stack orientation="horizontal" align="center" justify="space-between" space={3}>
      <StageNameWrapper>
        <Stack orientation="horizontal" align="center" space={3}>
          <IconStages size={20} color="textSecondary" />
          <Text font="semibold14" text={stageName} ellipsis />
        </Stack>
      </StageNameWrapper>
      <TriggerRowActions trigger={trigger} rowLabel={stageName} {...actionProps} />
    </Stack>
  </Card>
);

type OutcomeRowProps = TriggerRowActionsProps & {
  'data-cy'?: string;
  label: string;
  subtitle: string;
  outcomeType: JourneyOutcome;
};

const OutcomeRow = ({
  trigger,
  label,
  subtitle,
  outcomeType,
  onAdd,
  onReplace,
  onDelete,
  onEditConfig,
  isMutating,
  ...rest
}: OutcomeRowProps) => (
  <Card spaceX={4} spaceY={3} {...rest}>
    <Stack orientation="horizontal" align="center" justify="space-between" space={3}>
      <Stack orientation="horizontal" align="center" space={3}>
        {outcomeType === 'hired' ? (
          <IconHired size={20} color="green100" />
        ) : (
          <IconReject size={20} color="red100" />
        )}
        <Stack space={0}>
          <Text font="semibold14" text={label} />
          <Text font="regular12" color="textSecondary" text={subtitle} />
        </Stack>
      </Stack>
      <TriggerRowActions
        trigger={trigger}
        rowLabel={label}
        onAdd={onAdd}
        onReplace={onReplace}
        onDelete={onDelete}
        onEditConfig={onEditConfig}
        isMutating={isMutating}
      />
    </Stack>
  </Card>
);

type JourneyChipTriggerProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  journeyTitle: string;
  dataCy?: string;
};

// The chip clips at 220px, so the tooltip carries the full journey title —
// without it a long name is unreadable, and two similarly-prefixed journeys
// look identical. No separate aria-label: the visible Text below is
// already the chip's only content besides icons, so it already provides the
// accessible name — a matching aria-label would just repeat it verbatim.
const JourneyChipTrigger = forwardRef<HTMLButtonElement, JourneyChipTriggerProps>(
  ({ journeyTitle, dataCy, ...rest }, ref) => (
    <Tooltip content={journeyTitle}>
      <ChipButton ref={ref} type="button" data-cy={dataCy} {...rest}>
        <IconJourneyItem size={16} color="elementGreen100" />
        <Text font="semibold12" color="elementGreen200" text={journeyTitle} ellipsis />
        <IconChevronDownSmall color="elementGreen200" />
      </ChipButton>
    </Tooltip>
  ),
);

JourneyChipTrigger.displayName = 'JourneyChipTrigger';

// ---- Styled components ----

// The row's right-hand side (an optional "Unavailable" badge plus the journey
// chip, itself up to 220px) is natural-width content, so with only minWidth: 0
// here the stage name yielded every pixel first and collapsed to "Screeni…"
// while space was still free. flex: 1 claims the remaining width and the floor
// keeps enough of the name legible; the chip shrinks instead, which it already
// handles (minWidth: 0 plus an ellipsised label).
const StageNameWrapper = styled.div({
  flex: 1,
  minWidth: pxToRem(110),
  overflow: 'hidden',
});

// minWidth: 0 on the label is what makes the ellipsis work. The title is
// `white-space: nowrap`, so its min-content width is the whole string; as a
// flex item that cannot shrink it pushed the chevron past this chip's own
// overflow:hidden edge and the chevron was cut off — visible as soon as an
// "Unavailable" badge shared the row. Both icons already set flexShrink: 0
// (BaseIcon), so the label is the only part that yields.
const ChipButton = styled(RawButton)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.space[2],
  padding: `${theme.space[1]} ${theme.space[3]}`,
  borderRadius: pxToRem(100),
  backgroundColor: theme.color.greenTrans10,
  maxWidth: pxToRem(220),
  minWidth: 0,
  overflow: 'hidden',
  transition: transitionStandard('background-color'),
  '& > span': {
    minWidth: 0,
  },
  '&:hover': {
    backgroundColor: theme.color.greenTrans20,
  },
});

// The badge and chip are natural-width content, so this side of the row could
// not shrink below them and the row overflowed the card instead of narrowing
// the chip.
const RowActions = styled(Stack)({
  minWidth: 0,
});

const EmptyRow = styled.div({
  padding: `${pxToRem(8)} 0`,
});

const locale = {
  en: {
    title: 'Journey triggers',
    stageTriggers: 'Pipeline stage triggers',
    stageTriggersDescription: 'Trigger a journey when an applicant is moved into a pipeline stage.',
    outcomeTriggers: 'Final outcome triggers',
    outcomeTriggersDescription:
      'Trigger a journey when an applicant is either marked as hired or as rejected.',
    hired: 'Hired',
    hiredSubtitle: 'Trigger when applicant is hired',
    rejected: 'Rejected',
    rejectedSubtitle: 'Trigger when applicant is rejected',
    addJourney: 'Add journey',
    noStages: 'No pipeline stages',
    unknownStage: 'Unknown stage',
    loadError: 'Failed to load triggers. Please try again.',
    retry: 'Retry',
  },
  is: {
    title: 'Sjálfvirk ræsing Journey-ferla',
    stageTriggers: 'Ræsing eftir dálki',
    stageTriggersDescription:
      'Ræstu Journey-ferli þegar umsækjandi er færður í tiltekinn dálk í ráðningarferlinu.',
    outcomeTriggers: 'Ræsing við ráðningu eða höfnun',
    outcomeTriggersDescription:
      'Ræstu Journey-ferli þegar umsækjandi er merktur sem ráðinn eða umsókn hans er hafnað.',
    hired: 'Ráðinn',
    hiredSubtitle: 'Ræsist þegar umsækjandi er ráðinn',
    rejected: 'Hafnað',
    rejectedSubtitle: 'Ræsist þegar umsækjanda er hafnað',
    addJourney: 'Bæta við Journey-ferli',
    noStages: 'Engir dálkar í ráðningarferlinu',
    unknownStage: 'Óþekktur dálkur',
    loadError: 'Ekki tókst að sækja ræsingarreglur. Reyndu aftur.',
    retry: 'Reyna aftur',
  },
};
